import React, { useMemo } from "react";
import { useAppSelector } from "../../../redux/store";
import { User } from "../../../types";
import { useTranslation } from "react-i18next";
import Avatar from "../avatar/avatar";
import { getValidSpriteId, DEFAULT_AVATAR_ID } from "../../../utils/avatars";
import { useAuth } from "../../../hooks/useAuth";
import { AuthGuard } from "../../auth/AuthGuard";

function getRankInfo(user?: User) {
  if (!user) {
    return { display: "n/a", nameKey: "" };
  }

  if (user.rank >= 250) {
    return { display: "👑 S CLASS", nameKey: "body.rank5" };
  } else if (user.rank >= 100) {
    return { display: "⭐⭐⭐⭐⭐", nameKey: "body.rank4" };
  } else if (user.rank >= 50) {
    return { display: "⭐⭐⭐⭐", nameKey: "body.rank3" };
  } else if (user.rank >= 25) {
    return { display: "⭐⭐⭐", nameKey: "body.rank2" };
  } else if (user.rank >= 10) {
    return { display: "⭐⭐", nameKey: "body.rank1" };
  } else {
    return { display: "⭐", nameKey: "body.rank0" };
  }
}

function rank(user?: User) {
  return getRankInfo(user).display;
}

const AvatarProfile: React.FunctionComponent = () => {
  let playerDetails = useAppSelector((state) => state.menu.playerDetails);
  const { user } = useAuth();

  const { t } = useTranslation("main");

  function rankName(user?: User) {
    const rankInfo = getRankInfo(user);
    return rankInfo.nameKey ? t(rankInfo.nameKey) : "";
  }

  // Compute valid sprite ID based on user's premium status
  const validSpriteId = useMemo(() => {
    if (!playerDetails) return DEFAULT_AVATAR_ID;

    const savedSpriteId = playerDetails.meta.sprite_id;
    const userPremium = user?.premium ?? false;

    return getValidSpriteId(savedSpriteId, userPremium);
  }, [playerDetails, user?.premium]);

  return (
    <AuthGuard fallback={null}>
      <div className={`mx-2 flex flex-row relative z-10 mb-4 md:mx-12 overflow-hidden`}>
        <div
          className={
            "flex-grow z-30 flex justify-center items-center select-none"
          }
        >
          {/* Avatar is shown after auth resolves - using validated sprite ID based on premium status */}
          {(playerDetails || user !== undefined) && (
            <Avatar
              spriteId={validSpriteId}
              name={playerDetails?.name}
              bigText
              size="medium"
              variant="profile"
            />
          )}

          {user !== undefined && <div className={"flex-grow"} />}
        </div>

        {user !== undefined && (
          <div className={"absolute h-full w-full flex flex-row justify-end"}>
            <div
              className={
                "absolute bg-gray-drk h-4/5 top-[20%] w-3/4 max-w-md z-10 flex flex-col shadow-basic"
              }
            >
              <div className={"self-stretch h-8 bg-black"} />
            </div>

            <div className={"h-[192px] w-[192px]"} />

            <div
              className={
                "flex-grow z-20 h-4/5 self-end flex flex-col space-y-2"
              }
            >
              <div
                className={
                  "text-white bg-black self-start text-2xl h-8 middle flex justify-center items-center"
                }
              >
                {user.connection === "twitter" ? "@" : ""}
                {user.name !== undefined ? user.name.substring(0, 16) : "Guest"}
              </div>
              <div className={"flex-grow flex flex-col justify-around"}>
                <div
                  className={
                    "text-white bg-black self-start text-base h-6 flex justify-center items-center px-2"
                  }
                >
                  {t("body.supporterStatus")}:{" "}
                  {user.premium
                    ? t("body.supporterActive")
                    : t("body.supporterNone")}
                </div>
                <div
                  className={
                    "text-sm text-white bg-black self-start sm:text-base h-14 flex flex-col justify-around items-start px-2 "
                  }
                >
                  <div className={"leading-none"}>rank: {rank(user)}</div>
                  <div className={"leading-none"}>{rankName(user)}</div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </AuthGuard>
  );
};

export default AvatarProfile;
